import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import NotificationBell from '@/shared/components/layout/NotificationBell'
import UserMenu from '@/shared/components/layout/UserMenu'
import { useAuth } from '@/features/auth/hooks/useAuth'

interface Props {
  isDashboard: boolean
}

const routeLabels: Record<string, string> = {
  '/statements':          'Statement Generation',
  '/statements/':         'Generate Statements',  // prefix — matched below for /statements/:batchId
  '/pdf-upload':          'PDF Upload',
  '/pdf-upload/':         'PDF Upload',           // prefix — matched below for /pdf-upload/:batchId
  '/blast-emails':        'Blast Email',
  '/statement-templates': 'Statement Templates',
  '/email-templates':     'Email Templates',
  '/emails':              'Email',
  '/users':               'User Management',
  '/logs':                'Logs',
  '/profile':             'Profile',
}

export default function Header({ isDashboard }: Props) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // A detail route is `/{section}/{id}` where `/{section}/` has a prefix entry
  // in the route table; the crumb then reads Home > {section label} > {id}.
  const [, section = '', detailSegment = ''] = pathname.split('/')
  const parentPath = `/${section}`
  const isDetailPage = !!detailSegment && `${parentPath}/` in routeLabels
  const parentLabel = routeLabels[parentPath]
  const pageLabel =
    routeLabels[pathname] ??
    Object.entries(routeLabels).find(([k]) => k.endsWith('/') && pathname.startsWith(k))?.[1] ??
    'Dashboard'

  // ── Shared brand block ────────────────────────────────────────────────────
  const Brand = () => (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <div className="h-7 sm:h-8 flex items-center shrink-0">
        <img src="/logo-hc_global_02.png" alt="HC Global"
          className="h-full w-auto object-contain"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
      </div>
      <span className="text-gray-300 select-none hidden xs:inline">|</span>
      <span className="text-base sm:text-xl font-light text-gray-500 select-none tracking-wide truncate hidden sm:block">
        Investor Relations <span className="font-semibold text-primary">Portal</span>
      </span>
      <span className="text-sm font-semibold text-primary select-none sm:hidden">IR Portal</span>
    </div>
  )

  // ── Shared right user block ───────────────────────────────────────────────
  const UserBlock = () => (
    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
      <NotificationBell />
      <UserMenu
        name={user?.name ?? user?.email ?? ''}
        email={user?.email ?? ''}
        onProfile={() => navigate('/profile')}
        onLogout={logout}
      />
    </div>
  )

  // ── Dashboard header ──────────────────────────────────────────────────────
  if (isDashboard) {
    return (
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-200 gap-3">
        <Brand />
        <UserBlock />
      </header>
    )
  }

  // ── Sub-page header — breadcrumb ──────────────────────────────────────────
  return (
    <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-200 gap-3">
      <nav className="flex items-center gap-1 text-sm text-gray-500 min-w-0 overflow-hidden">
        <Link to="/" className="hover:text-primary transition-colors shrink-0">Home</Link>
        <ChevronRight size={14} className="shrink-0" />
        {isDetailPage ? (
          <>
            <Link to={parentPath} className="hover:text-primary transition-colors hidden sm:inline shrink-0">
              {parentLabel}
            </Link>
            <ChevronRight size={14} className="shrink-0 hidden sm:inline" />
            <span className="text-gray-800 font-medium font-mono truncate">{detailSegment}</span>
          </>
        ) : (
          <span className="text-gray-800 font-medium truncate">{pageLabel}</span>
        )}
      </nav>

      <UserBlock />
    </header>
  )
}

